import config from '@/data/config';

import { backoffice as backofficeAPI } from '@/services';

const STORAGE_KEY = 'reloadedForVersion';
// just a backstop now — the X-Deploy-Version-Admin response header (see
// services/api.js) is the primary, near-immediate detection path
const MIN_INTERVAL_MS = 5 * 60 * 60 * 1000;

// caps automatic reloads (localStorage, shared across tabs) so a broken deploy —
// e.g. CDN/S3 serving a stale index.html whose bundled version never matches what
// the header reports — can't turn into an infinite auto-reload loop
const RELOAD_ATTEMPTS_KEY = 'autoReloadAttempts';
const RELOAD_ATTEMPTS_WINDOW_MS = 2 * 60 * 1000;
const RELOAD_ATTEMPTS_MAX = 2;

let lastCheckedAt = 0;
let inflight = null;

function readReloadAttempts() {
  try {
    const stored = JSON.parse(localStorage.getItem(RELOAD_ATTEMPTS_KEY));
    if (!stored || Date.now() - stored.windowStart > RELOAD_ATTEMPTS_WINDOW_MS) {
      return { count: 0, windowStart: Date.now() };
    }
    return stored;
  } catch {
    return { count: 0, windowStart: Date.now() };
  }
}

function evaluateVersion(rawDeployVersion) {
  const deployVersion = String(rawDeployVersion ?? '');
  const localVersion = String(config?.DEPLOY_VERSION ?? '');
  const reloadedForVersion = localStorage.getItem(STORAGE_KEY);

  if (!deployVersion || !localVersion) {
    return { scheduled: false };
  }

  if (deployVersion === localVersion) {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(RELOAD_ATTEMPTS_KEY);
    return { scheduled: false };
  }

  if (reloadedForVersion === deployVersion) {
    return { scheduled: false };
  }

  localStorage.setItem(STORAGE_KEY, deployVersion);

  return { scheduled: true };
}

async function runValidation() {
  try {
    const { data } = await backofficeAPI.getAPPVersion(Date.now());

    return evaluateVersion(data?.data?.version);
  } catch {
    return { scheduled: false };
  }
}

export function useAppVersion() {
  async function validateAppVersion({ force = false } = {}) {
    // dedupe concurrent calls
    if (inflight) return inflight;

    // skip if a recent check already happened
    if (!force && Date.now() - lastCheckedAt < MIN_INTERVAL_MS) {
      return { scheduled: false };
    }

    inflight = runValidation().finally(() => {
      lastCheckedAt = Date.now();
      inflight = null;
    });

    return inflight;
  }

  // same decision logic as validateAppVersion, fed by an observed header instead of a poll
  function reportObservedVersion(rawDeployVersion) {
    return evaluateVersion(rawDeployVersion);
  }

  function clearVersionGuard() {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(RELOAD_ATTEMPTS_KEY);
  }

  // must be called right before an automatic window.location.reload() — returns
  // false once the cap is hit, telling the caller to give up and show the manual
  // notice instead of reloading again
  function registerAutoReload() {
    const attempts = readReloadAttempts();
    if (attempts.count >= RELOAD_ATTEMPTS_MAX) return false;
    localStorage.setItem(RELOAD_ATTEMPTS_KEY, JSON.stringify({
      count: attempts.count + 1,
      windowStart: attempts.windowStart,
    }));
    return true;
  }

  return {
    validateAppVersion, reportObservedVersion, clearVersionGuard, registerAutoReload,
  };
}
